import React, { useState, useEffect } from 'react';
import { baseUrl, assetFullLabel } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const EMPTY_FORM = {
  customer: '',
  catNo: '',
  assetId: '',
  jobType: 'REPAIR',
  location: 'WORKSHOP',
  status: 'IN_TRANSIT',
  approval: 'PENDING',
  dateReceived: null,
  customerPO: '',
  quoteNo: '',
  reqNo: '',
  refNo: '',
  serviceFrequency: '',
  jobAssignedTo: '',
  quotedExGst: '',
  quotedHrs: '',
  defect: '',
};

export default function WorkshopJobModal({ open, onClose, job, companies, onSaved, initialData }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [assets, setAssets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [internalCompanies, setInternalCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const resolvedCompanies = companies?.length ? companies : internalCompanies;

  useEffect(() => {
    if (!open) return;
    fetch(baseUrl + 'workshop/staff', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setStaff(Array.isArray(d) ? d : []));
    if (!companies?.length) {
      fetch(baseUrl + 'company/list', { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setInternalCompanies(Array.isArray(d) ? d : []));
    }
  }, [open]);

  useEffect(() => {
    if (open && job) {
      setForm({
        customer: job.customer || '',
        catNo: job.catNo || '',
        assetId: job.assetId || '',
        jobType: job.jobType || 'REPAIR',
        location: job.location || 'WORKSHOP',
        status: job.status || 'IN_TRANSIT',
        approval: job.approval || 'PENDING',
        dateReceived: job.dateReceived ? dayjs(job.dateReceived) : null,
        customerPO: job.customerPO || '',
        quoteNo: job.quoteNo || '',
        reqNo: job.reqNo || '',
        refNo: job.refNo || '',
        serviceFrequency: job.serviceFrequency || '',
        jobAssignedTo: job.jobAssignedTo || '',
        quotedExGst: job.quotedExGst || '',
        quotedHrs: job.quotedHrs || '',
        defect: job.defect || '',
      });
    } else if (open && !job) {
      setForm(initialData ? { ...EMPTY_FORM, ...initialData } : EMPTY_FORM);
    }
    setErrors({});
  }, [open, job]);

  useEffect(() => {
    if (!form.customer) { setAssets([]); return; }
    fetch(baseUrl + 'workshop/assets?customer=' + encodeURIComponent(form.customer), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAssets(Array.isArray(d) ? d : []));
  }, [form.customer]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.jobType) e.jobType = 'Required';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true);
    const body = {
      ...form,
      dateReceived: form.dateReceived ? form.dateReceived.format('YYYY-MM-DD') : null,
      quotedExGst: form.quotedExGst !== '' ? form.quotedExGst : null,
      quotedHrs: form.quotedHrs !== '' ? form.quotedHrs : null,
    };
    const url = job
      ? baseUrl + 'workshop/jobs/' + job.id
      : baseUrl + 'workshop/jobs';
    const method = job ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedAsset = assets.find(a => a.catNo === form.catNo) || null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{job ? 'Edit Job' : 'New Workshop Job'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Job Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!errors.jobType}>
                <InputLabel>Job Type *</InputLabel>
                <Select value={form.jobType} label="Job Type *" onChange={set('jobType')}>
                  <MenuItem value="REPAIR">Repair</MenuItem>
                  <MenuItem value="SUPPLY">Supply</MenuItem>
                  <MenuItem value="CALIBRATION">Calibration</MenuItem>
                  <MenuItem value="SITE_VISIT">Site Visit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select value={form.location} label="Location" onChange={set('location')}>
                  <MenuItem value="WORKSHOP">Workshop</MenuItem>
                  <MenuItem value="SITE">Site</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={set('status')}>
                  {['IN_TRANSIT','TEARDOWN','TEARDOWN_COMPLETE','QUOTE_SENT','IN_PROGRESS','COMPLETE','CANCELLED'].map(s => (
                    <MenuItem key={s} value={s}>
                      {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Approval</InputLabel>
                <Select value={form.approval} label="Approval" onChange={set('approval')}>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="NOT_APPROVED">Not Approved</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Customer & Asset
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={resolvedCompanies.map(c => c.company)}
                value={form.customer || null}
                onChange={(_, val) => setForm(f => ({ ...f, customer: val || '', catNo: '', assetId: '' }))}
                renderInput={params => <TextField {...params} label="Customer" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={assets}
                getOptionLabel={a => `${assetFullLabel(a)} — ${a.item || ''}`}
                value={selectedAsset}
                onChange={(_, val) => setForm(f => ({
                  ...f,
                  catNo: val ? val.catNo : '',
                  assetId: val ? (val.assetId || '') : '',
                }))}
                disabled={!form.customer}
                renderInput={params => <TextField {...params} label="Asset (Asset ID)" />}
                noOptionsText={form.customer ? 'No assets found' : 'Select a customer first'}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Reference Numbers
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date Received"
                value={form.dateReceived}
                onChange={val => setForm(f => ({ ...f, dateReceived: val }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                format="DD/MM/YYYY"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Customer PO" value={form.customerPO} onChange={set('customerPO')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Quote No" value={form.quoteNo} onChange={set('quoteNo')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Req No" value={form.reqNo} onChange={set('reqNo')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Ref No" value={form.refNo} onChange={set('refNo')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Service Frequency" value={form.serviceFrequency} onChange={set('serviceFrequency')} />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Assignment & Costs
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Assigned To</InputLabel>
                <Select value={form.jobAssignedTo} label="Assigned To" onChange={set('jobAssignedTo')}>
                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                  {staff.map(s => (
                    <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                size="small" fullWidth label="Quoted Ex GST ($)"
                type="number" value={form.quotedExGst} onChange={set('quotedExGst')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                size="small" fullWidth label="Quoted Hours"
                type="number" value={form.quotedHrs} onChange={set('quotedHrs')}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Notes
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small" fullWidth multiline rows={3}
                label="Defect / Notes" value={form.defect} onChange={set('defect')}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
          <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : job ? 'Save Changes' : 'Create Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
